'use client';

import PropTypes from 'prop-types';
import React, { useMemo } from 'react';

import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import { useTheme, decomposeColor, alpha as hexAlpha } from '@mui/material/styles';

import { formatNumber } from 'src/utils/format/format-utils';

import { RADIUS } from 'src/theme/ui-tokens';
import { languageMap } from 'src/constants/languages';
import platforms from 'src/constants/platform-catalog';

import { SegmentTabs } from 'src/components/cmui/segment-tabs';


const PLATFORM_CONFIG_MAP = new Map(
  (platforms || []).map((platform) => [String(platform.id).toLowerCase(), platform]),
);

const LANGUAGE_CONFIG_MAP = new Map(
  (languageMap || []).map((lang) => [String(lang.code).toLowerCase(), lang]),
);

/** 열이 더 늘어나면 셀이 읽을 수 없이 좁아져서, 나머지는 '기타'로 접는다 */
const MAX_COLUMNS = 8;
const OTHERS_COLUMN_KEY = '__others__';

const ROW_HEIGHT = 28;
const HEADER_HEIGHT = 40;
const LABEL_COLUMN_WIDTH = 140;
const TOTAL_COLUMN_WIDTH = 50;
const MIN_CELL_WIDTH = 50;

function resolveColumnLabel(key: string, isMarketMode: boolean, custom?: (key: string) => string) {
  if (key === OTHERS_COLUMN_KEY) return '기타';
  // 열이 채널·국가가 아닌 축일 때 — 카탈로그가 아니라 부르는 쪽이 이름을 안다
  if (custom) return custom(key);
  if (isMarketMode) {
    if (key === 'others') return '🌏 기타';
    return LANGUAGE_CONFIG_MAP.get(key)?.name || key.toUpperCase();
  }
  return PLATFORM_CONFIG_MAP.get(key)?.label || key;
}

/**
 * 셀 색 — 단일 색상 순차 램프.
 * 행(브랜드) 내 최대값 기준으로 정규화해 "이 브랜드가 어디에 몰려 있나"를 읽게 한다.
 */
function resolveCellIntensity(value: number, rowMax: number) {
  if (!value || rowMax <= 0) return 0;
  return Math.min(1, value / rowMax);
}

const MIN_CELL_ALPHA = 0.08;
const MAX_CELL_ALPHA = 0.95;

/** 램프 끝 색 — 라이트는 어두워지는 방향, 다크는 밝아지는 방향이어야 "많음"으로 읽힌다 */
function resolveRampEndColor(theme: any) {
  return theme.palette.mode === 'dark'
    ? theme.palette.primary.light
    : theme.palette.primary.dark;
}

function relativeLuminance([r, g, b]: readonly number[]) {
  const channel = (value: number) => {
    const s = value / 255;
    return s <= 0.03928 ? s / 12.92 : ((s + 0.055) / 1.055) ** 2.4;
  };
  return 0.2126 * channel(r) + 0.7152 * channel(g) + 0.0722 * channel(b);
}

/**
 * 셀 글자색은 고정 임계값이 아니라 실제 합성된 배경 밝기로 정한다 —
 * primary.main(#10AFEA)처럼 밝은 브랜드 색에서 흰 글씨가 깨지는 걸 막는다.
 */
function resolveCellTextColor(theme: any, rampEnd: string, alphaValue: number) {
  const fill = decomposeColor(rampEnd).values;
  const paper = decomposeColor(theme.palette.background.paper).values;
  const composited = [0, 1, 2].map(
    (i) => fill[i] * alphaValue + paper[i] * (1 - alphaValue),
  );
  return relativeLuminance(composited) > 0.42
    ? theme.palette.grey[900]
    : theme.palette.common.white;
}

/**
 * 격자 히트맵 — 대시보드의 「브랜드별 히트맵」이자, **맞춤 대시보드의 히트맵 칸**이기도 하다.
 *
 * 🔑 **히트맵은 이 하나뿐이다** — 축만 갈아 끼운다. 따로 그리면 같은 이름의 그림이 자리마다
 *   달라진다(그래서 열 이름·제목·모드 칩을 밖에서 정할 수 있게 열어 두었다).
 * 🔑 **색은 «줄 안에서» 정규화한다** — 「이 줄이 어디에 몰려 있나」를 읽는 그림이다.
 * ⚠️ 열은 여덟까지만 편다(`MAX_COLUMNS`) — 나머지는 「기타」 한 칸으로 접는다. 더 펴면 칸이
 *   읽을 수 없이 좁아진다.
 */
function BrandChannelHeatmap({
  rows = [],
  mode = 'market',
  onModeChange,
  onCellClick,
  maxRows = 10,
  title = '브랜드별 히트맵',
  columnLabel,
  showModeChips = true,
  emptyText = '브랜드 데이터가 없습니다.',
}: {
  rows?: readonly {
    name?: string
    total?: number
    bySource?: Record<string, unknown>
    byMarket?: Record<string, unknown>
  }[]
  mode?: 'platform' | 'market'
  onModeChange?: (...args: any[]) => void
  onCellClick?: (...args: any[]) => void
  maxRows?: number
  /** 그림 제목 — 빈 글자면 제목 줄을 안 그린다 */
  title?: string
  /**
   * 열 이름 — **채널·국가가 아닌 축**을 쓸 때 준다(브랜드·해시태그·날짜…).
   * ⚠️ 주면 채널 아이콘도 안 붙인다 — 브랜드 이름 위에 채널 아이콘이 서면 거짓말이 된다.
   */
  columnLabel?: (key: string) => string
  /** 「채널」/「국가」 칩 — 축이 고정된 자리에서는 끈다 */
  showModeChips?: boolean
  emptyText?: string
}) {
  const theme = useTheme();
  const isMarketMode = mode === 'market';
  const rampEndColor = resolveRampEndColor(theme);

  const { visibleRows, columns } = useMemo(() => {
    const list = (Array.isArray(rows) ? rows : [])
      .filter((row) => String(row?.name || '').trim())
      .slice(0, maxRows);

    if (!list.length) return { visibleRows: [], columns: [] };

    const pickCounts = (row: any): Record<string, unknown> => (isMarketMode ? row?.byMarket : row?.bySource) || {};

    // 열 순서는 전체 합계 기준 — 브랜드마다 열이 흔들리면 비교가 불가능하다
    const columnTotals = new Map<string, number>();
    list.forEach((row) => {
      Object.entries(pickCounts(row)).forEach(([key, value]) => {
        const count = Number(value) || 0;
        if (count <= 0) return;
        columnTotals.set(key, (columnTotals.get(key) ?? 0) + count);
      });
    });

    const orderedKeys = Array.from(columnTotals.entries())
      .sort((a, b) => b[1] - a[1])
      .map(([key]) => key);

    const keptKeys = orderedKeys.slice(0, MAX_COLUMNS);
    const foldedKeys = orderedKeys.slice(MAX_COLUMNS);

    const nextColumns = keptKeys.map((key) => ({
      key,
      label: resolveColumnLabel(key, isMarketMode, columnLabel),
      // ⚠️ 이름을 밖에서 주는 축에는 채널 아이콘을 안 붙인다(위 prop 머리말)
      iconSrc: isMarketMode || columnLabel ? null : `/assets/icons/platforms/${key}.svg`,
    }));
    if (foldedKeys.length > 0) {
      nextColumns.push({
        key: OTHERS_COLUMN_KEY,
        label: `기타 ${foldedKeys.length}개`,
        iconSrc: null,
      });
    }

    const nextRows = list.map((row) => {
      const counts = pickCounts(row);
      const cells = nextColumns.map((column) => {
        if (column.key === OTHERS_COLUMN_KEY) {
          const value = foldedKeys.reduce(
            (sum, key) => sum + (Number(counts[key]) || 0),
            0,
          );
          return { key: column.key, value, drillable: false };
        }
        return {
          key: column.key,
          value: Number(counts[column.key]) || 0,
          drillable: true,
        };
      });

      const rowMax = cells.reduce((max, cell) => Math.max(max, cell.value), 0);
      const rowTotal = Number(row.total)
        || cells.reduce((sum, cell) => sum + cell.value, 0);

      return { name: String(row.name).trim(), total: rowTotal, cells, rowMax };
    });

    return { visibleRows: nextRows, columns: nextColumns };
  }, [rows, isMarketMode, maxRows, columnLabel]);

  const gridTemplateColumns = `${LABEL_COLUMN_WIDTH}px repeat(${columns.length}, minmax(${MIN_CELL_WIDTH}px, 1fr)) ${TOTAL_COLUMN_WIDTH}px`;

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      {(title || showModeChips) && (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 1.5,
            flexWrap: 'wrap',
          }}
        >
          <Box>
            {title && (
              <Typography
                variant="h6"
                sx={{ fontWeight: 600, fontSize: { xs: '20px', sm: '1.25rem' } }}
              >
                {title}
              </Typography>
            )}
          </Box>
          {showModeChips && (
            <SegmentTabs
              value={isMarketMode ? 'market' : 'platform'}
              onChange={(next) => onModeChange?.(next)}
              tabs={[
                { key: 'platform', label: '채널' },
                { key: 'market', label: '국가' },
              ]}
            />
          )}
        </Box>
      )}

      {visibleRows.length === 0 || columns.length === 0 ? (
        <Box
          sx={{
            flex: 1,
            minHeight: 240,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <Typography variant="body2" color="text.secondary">
            {emptyText}
          </Typography>
        </Box>
      ) : (
        <Box sx={{ width: '100%', overflowX: 'auto', pb: 0.5 }}>
          <Box
            sx={{
              minWidth:
                LABEL_COLUMN_WIDTH
                + TOTAL_COLUMN_WIDTH
                + columns.length * MIN_CELL_WIDTH,
            }}
          >
            {/* 헤더 */}
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns,
                gap: '2px',
                alignItems: 'end',
                height: HEADER_HEIGHT,
                mb: '2px',
              }}
            >
              <Box />
              {columns.map((column) => (
                <Stack
                  key={column.key}
                  spacing={0.25}
                  alignItems="center"
                  justifyContent="flex-end"
                  sx={{ minWidth: 0, pb: 0.5 }}
                >
                  {column.iconSrc && (
                    <Box
                      component="img"
                      src={column.iconSrc}
                      alt=""
                      sx={{ width: 14, height: 14, objectFit: 'contain' }}
                      onError={(event) => {
                        event.currentTarget.style.display = 'none';
                      }}
                    />
                  )}
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{
                      maxWidth: '100%',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                      typography: 'uiMeta',
                    }}
                    title={column.label}
                  >
                    {column.label}
                  </Typography>
                </Stack>
              ))}
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ textAlign: 'right', pb: 0.5, fontSize: '11px' }}
              >
                합계
              </Typography>
            </Box>

            {/* 셀 */}
            {visibleRows.map((row) => (
              <Box
                key={row.name}
                sx={{
                  display: 'grid',
                  gridTemplateColumns,
                  gap: '2px',
                  mb: '2px',
                  height: ROW_HEIGHT,
                  alignItems: 'stretch',
                }}
              >
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    minWidth: 0,
                    pr: 1,
                  }}
                >
                  <Typography
                    variant="body2"
                    sx={{
                      fontWeight: 500,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}
                    title={row.name}
                  >
                    {row.name}
                  </Typography>
                </Box>

                {row.cells.map((cell) => {
                  const intensity = resolveCellIntensity(cell.value, row.rowMax);
                  const cellAlpha = MIN_CELL_ALPHA
                    + intensity * (MAX_CELL_ALPHA - MIN_CELL_ALPHA);
                  const share = row.total > 0
                    ? Math.round((cell.value / row.total) * 100)
                    : 0;
                  const isInteractive = cell.value > 0 && cell.drillable;
                  const cellColumnLabel = columns.find((c) => c.key === cell.key)?.label
                    ?? cell.key;

                  return (
                    <Tooltip
                      key={`${row.name}-${cell.key}`}
                      arrow
                      title={
                        cell.value > 0
                          ? `${row.name} · ${cellColumnLabel} — ${formatNumber(cell.value)}건 (브랜드 내 ${share}%)`
                          : `${row.name} · ${cellColumnLabel} — 멘션 없음`
                      }
                    >
                      <Box
                        role={isInteractive ? 'button' : undefined}
                        tabIndex={isInteractive ? 0 : undefined}
                        onClick={
                          isInteractive
                            ? () => onCellClick?.(row.name, cell.key, mode, cellColumnLabel)
                            : undefined
                        }
                        onKeyDown={
                          isInteractive
                            ? (event) => {
                              if (event.key === 'Enter' || event.key === ' ') {
                                event.preventDefault();
                                onCellClick?.(row.name, cell.key, mode, cellColumnLabel);
                              }
                            }
                            : undefined
                        }
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          borderRadius: RADIUS.sm,
                          cursor: isInteractive ? 'pointer' : 'default',
                          bgcolor: cell.value > 0
                            ? hexAlpha(rampEndColor, cellAlpha)
                            : 'action.hover',
                          transition: 'outline-color 0.15s ease',
                          outline: '2px solid transparent',
                          outlineOffset: '-2px',
                          '&:hover': isInteractive
                            ? { outlineColor: theme.palette.primary.dark }
                            : undefined,
                        }}
                      >
                        <Typography
                          variant="caption"
                          sx={{
                            fontWeight: 600,
                            fontVariantNumeric: 'tabular-nums',
                            color: cell.value === 0
                              ? 'text.disabled'
                              : resolveCellTextColor(theme, rampEndColor, cellAlpha),
                          }}
                        >
                          {cell.value > 0 ? formatNumber(cell.value) : '·'}
                        </Typography>
                      </Box>
                    </Tooltip>
                  );
                })}

                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'flex-end',
                    pl: 1,
                  }}
                >
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}
                  >
                    {formatNumber(row.total)}
                  </Typography>
                </Box>
              </Box>
            ))}
          </Box>
        </Box>
      )}

    </Box>
  );
}

BrandChannelHeatmap.propTypes = {
  rows: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string,
      total: PropTypes.number,
      bySource: PropTypes.object,
      byMarket: PropTypes.object,
    }),
  ),
  mode: PropTypes.oneOf(['platform', 'market']),
  onModeChange: PropTypes.func,
  onCellClick: PropTypes.func,
  maxRows: PropTypes.number,
  title: PropTypes.string,
  columnLabel: PropTypes.func,
  showModeChips: PropTypes.bool,
  emptyText: PropTypes.string,
};

export default React.memo(BrandChannelHeatmap);
